/**
 * HOY — F10.1
 *
 * «¿Qué está pasando hoy en mi centro?» dentro de Inicio, no en una pantalla nueva: Inicio ya es
 * donde se llega y sólo repetía el menú. Los datos son los del Planner (`/planificacion/planner`
 * del día, sin proyecciones): UNA petición trae las OT con su estado efectivo, horario, asignados
 * por nombre, alertas, avance y NC abiertas — nada se pide OT a OT. Sólo «Ver tareas» pide la
 * ficha de UNA OT, a demanda.
 *
 * AVANCE ≠ CALIDAD: avance = líneas ejecutadas (CONFIRMADA/VERIFICADA/NO_OK/NO_APLICA) / totales.
 * Una OT con una línea No OK puede estar al 100 % y, aparte, pedir atención de calidad.
 *
 * Centro, fecha y filtro viven en la URL de Inicio: abrir una OT y volver devuelve exactamente aquí.
 *
 * Checkpoint post-F14.3 — la tarjeta dice QUÉ trabajo es (la primera tarea de la OT y «N más», y la sala), y un
 * buscador filtra las OT del día por número, tarea, ubicación u operario. Sin búsqueda en el servidor: Inicio ya trae
 * TODAS las OT de un centro y un día en una sola respuesta sin paginar, y buscar es filtrar esa respuesta. El texto vive
 * en la URL con cliente, centro y fecha (una sola fuente de verdad): al cambiar de ámbito se filtra la respuesta NUEVA,
 * y mientras llega no se enseña la del ámbito anterior.
 */
import { type ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { api, type HoyMantenimiento, type OtDetalle, type ParteResumen, type PlannerEventoMaterializado, type ResultadoPlanner } from '../api.ts';
import {
  Cargando,
  Error as ErrorCaja,
  EstadoLinea,
  EstadoOt,
  hoyLocal,
  nombresOperarios,
  rangoHorario,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { SelectorCentroTrabajo, useCentroEnUrl } from '../componentes/centro-trabajo.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { PrioridadEtiqueta, useVolverAqui } from '../componentes/mantenimiento.tsx';
import { useSesion } from '../contexto.tsx';
import { TarjetaParte } from './partes.tsx';
import { Alertas } from './preventivos.tsx';

const DEFAULTS_HOY = { idCliente: '', idCentro: '', fecha: '', ver: '', area: '', mnt: '', q: '' };

type Grupo = 'todas' | 'pendientes' | 'en_curso' | 'completadas' | 'atencion';

const ESTADOS_COMPLETADA = new Set(['COMPLETADA', 'VERIFICADA', 'CERRADA', 'REABIERTA']);

function grupoDeEstado(estado: string): Grupo | null {
  if (estado === 'PLANIFICADA') return 'pendientes';
  if (estado === 'EN_CURSO') return 'en_curso';
  if (ESTADOS_COMPLETADA.has(estado)) return 'completadas';
  return null;
}

export interface Atencion {
  readonly clave: string;
  readonly texto: string;
  readonly tono: 'error' | 'aviso';
}

/**
 * Sólo atención REAL (F10.1): sin asignar, vencida, No OK pendiente y NC abierta. Nunca «ha llegado
 * la hora de inicio». Sin asignar y vencida conservan la severidad que ya calcula el Planner.
 */
export function atencionDeOt(ev: PlannerEventoMaterializado): Atencion[] {
  const r: Atencion[] = [];
  const tono = (s: string): 'error' | 'aviso' => (s === 'ALTA' ? 'error' : 'aviso');
  const sinAsignar = ev.alertas.find((a) => a.tipo === 'OT_SIN_ASIGNAR');
  if (sinAsignar !== undefined) r.push({ clave: 'sin-asignar', texto: 'Sin asignar', tono: tono(sinAsignar.severidad) });
  const vencida = ev.alertas.find((a) => a.tipo === 'OT_VENCIDA');
  if (vencida !== undefined) r.push({ clave: 'vencida', texto: 'Vencida', tono: tono(vencida.severidad) });
  const noOk = ev.avance?.lineasNoOk ?? 0;
  if (noOk > 0) r.push({ clave: 'no-ok', texto: noOk === 1 ? 'No OK pendiente' : `${String(noOk)} No OK pendientes`, tono: 'error' });
  if ((ev.ncAbiertas ?? 0) > 0) r.push({ clave: 'nc', texto: 'NC abierta', tono: 'aviso' });
  return r;
}

/** «07:00–08:30» con la hora LOCAL del centro que ya trae el evento; «Sin hora» si no se conoce. */
export function horarioDeOt(ev: PlannerEventoMaterializado): string {
  if (ev.inicioProgramadoDesconocido || ev.inicioLocal === null) return 'Sin hora';
  const [h, m] = ev.inicioLocal.slice(11, 16).split(':').map(Number);
  //  F10.4: si termina al día siguiente, lo dice («(+1 día)»).
  return rangoHorario((h ?? 0) * 60 + (m ?? 0), ev.duracionPlanificadaMin);
}

/** Qué trabajo es: la primera tarea de la OT y cuántas más tiene (distintas). `null` si la OT no tiene tareas. */
export function trabajoDeOt(ev: PlannerEventoMaterializado): { titulo: string; mas: number } | null {
  const tareas = ev.tareas ?? [];
  if (tareas.length === 0) return null;
  return { titulo: tareas[0]!, mas: tareas.length - 1 };
}

/** Minúsculas, sin tildes ni espacios sobrantes: «Desinfección » y «desinfeccion» son lo mismo al buscar. */
export function normalizarBusqueda(texto: string): string {
  return texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * ¿La OT responde a lo buscado? Cada palabra tiene que aparecer (parcial vale: «151» encuentra OT-00000151) en el
 * número, sus tareas, la sala, las ubicaciones de sus tareas o sus asignados. Vacío = todas.
 */
export function coincideBusqueda(ev: PlannerEventoMaterializado, busqueda: string): boolean {
  const palabras = normalizarBusqueda(busqueda).split(' ').filter((p) => p !== '');
  if (palabras.length === 0) return true;
  const texto = normalizarBusqueda([
    ev.codigo, ev.codigoSala, ev.descripcionSala ?? '', ...(ev.tareas ?? []), ...(ev.ubicacionesTareas ?? []),
    ...ev.operarios.flatMap((o) => [o.nombre, o.codigo]),
  ].join(' | '));
  return palabras.every((p) => texto.includes(p));
}

export function textoAvance(ev: PlannerEventoMaterializado): string {
  const total = ev.avance?.lineasTotales ?? 0;
  const hechas = ev.avance?.lineasEjecutadas ?? 0;
  if (total === 0) return 'Sin tareas';
  return `${String(hechas)}/${String(total)} tareas · ${String(Math.round((hechas * 100) / total))} %`;
}

export function SeccionHoy(): ReactNode {
  const lista = useListaUrl(DEFAULTS_HOY);
  const idCliente = lista.valor('idCliente');
  const idCentro = lista.valor('idCentro');
  const hoy = hoyLocal(0);
  const fecha = lista.valor('fecha') || hoy;
  const ver = (lista.valor('ver') || 'todas') as Grupo;
  const inicial = useCentroEnUrl(idCliente, idCentro, (c) => lista.fijarVarios(c));
  //  F12.2: Limpieza + Mantenimiento en el mismo «Hoy», con el mismo centro y fecha (pestaña en la URL).
  const { puede } = useSesion();
  const conMantenimiento = puede('partes:leer');
  const area = conMantenimiento && lista.valor('area') === 'mnt' ? 'mnt' : 'limpieza';

  const busqueda = lista.valor('q');
  const datos = useDatos(
    () =>
      idCentro === ''
        ? Promise.resolve(null)
        : api.get<ResultadoPlanner>(`/api/centros/${idCentro}/planificacion/planner?desde=${fecha}&hasta=${fecha}&proyecciones=false`),
    [idCentro, fecha],
  );
  //  Sólo la respuesta del ámbito ACTUAL: mientras llega la del nuevo centro o día, la anterior no se enseña ni se busca.
  const delAmbito = datos.datos !== null && datos.datos.idCentro === idCentro && datos.datos.desde === fecha ? datos.datos : null;

  const todasDelDia = (delAmbito?.eventos ?? [])
    .filter((e): e is PlannerEventoMaterializado => e.tipo === 'OT')
    .sort((a, b) => (a.inicioLocal ?? '99').localeCompare(b.inicioLocal ?? '99') || a.codigo.localeCompare(b.codigo));
  const ots = todasDelDia.filter((o) => coincideBusqueda(o, busqueda));
  const buscando = normalizarBusqueda(busqueda) !== '';
  const cuenta = (g: Grupo): number =>
    g === 'todas' ? ots.length : g === 'atencion' ? ots.filter((o) => atencionDeOt(o).length > 0).length : ots.filter((o) => grupoDeEstado(o.estado) === g).length;
  const visibles = ots.filter((o) =>
    ver === 'todas' ? true : ver === 'atencion' ? atencionDeOt(o).length > 0 : grupoDeEstado(o.estado) === ver,
  );
  const indicadores: readonly { g: Grupo; texto: string }[] = [
    { g: 'todas', texto: fecha === hoy ? 'OT de hoy' : 'OT del día' },
    { g: 'pendientes', texto: 'Pendientes' },
    { g: 'en_curso', texto: 'En curso' },
    { g: 'completadas', texto: 'Completadas' },
    { g: 'atencion', texto: 'Requieren atención' },
  ];

  return (
    <section className="panel" aria-labelledby="hoy-titulo">
      <div className="panel-cabecera">
        <h2 id="hoy-titulo">{fecha === hoy ? 'Hoy' : 'Día'}</h2>
      </div>
      <div className="panel-cuerpo">
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <SelectorCentroTrabajo
            idPrefijo="hoy"
            idCliente={idCliente}
            idCentro={idCentro}
            inicial={inicial}
            fijar={(c) => lista.fijarVarios(c)}
          />
          <div className="campo">
            <label htmlFor="hoy-fecha">Fecha</label>
            <input id="hoy-fecha" type="date" style={{ minHeight: 44 }} value={fecha} onChange={(e) => lista.fijarVarios({ fecha: e.target.value === hoy ? '' : e.target.value })} />
          </div>
          {fecha !== hoy && (
            <button type="button" style={{ minHeight: 44 }} onClick={() => lista.fijarVarios({ fecha: '' })}>
              Volver a hoy
            </button>
          )}
        </div>

        {conMantenimiento && (
          <div className="pestanas-hoy fila" role="tablist" aria-label="Área" style={{ gap: 8, marginTop: 12 }}>
            {([['limpieza', 'Limpieza'], ['mnt', 'Mantenimiento']] as const).map(([k, t]) => (
              <button key={k} type="button" role="tab" aria-selected={area === k} className={area === k ? 'principal' : 'secundario'}
                onClick={() => lista.fijarVarios({ area: k === 'mnt' ? 'mnt' : '' })}>{t}</button>
            ))}
          </div>
        )}
        {idCentro === '' && <Vacio titulo="Elige un centro para ver su día." />}
        {area === 'mnt' && idCentro !== '' && (
          <BloqueMantenimiento idCentro={idCentro} fecha={fecha === hoy ? '' : fecha} grupo={lista.valor('mnt')} fijarGrupo={(g) => lista.fijarVarios({ mnt: g })} />
        )}
        {area === 'limpieza' && idCentro !== '' && datos.error === null && delAmbito === null && <Cargando texto="Cargando el día…" />}
        {area === 'limpieza' && idCentro !== '' && datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}

        {area === 'limpieza' && idCentro !== '' && datos.error === null && delAmbito !== null && (
          <>
            <div className="hoy-buscador" role="search">
              <input
                id="hoy-buscar"
                type="search"
                aria-label="Buscar OT"
                placeholder="Buscar OT, tarea o ubicación…"
                value={busqueda}
                autoComplete="off"
                onChange={(e) => lista.fijarVarios({ q: e.target.value })}
              />
              {buscando && (
                <p className="suave pequena" role="status">
                  {ots.length === 1 ? '1 OT' : `${String(ots.length)} OT`} de {String(todasDelDia.length)} coinciden con «{busqueda.trim()}».
                </p>
              )}
            </div>
            <div className="fila" role="group" aria-label="Resumen del día" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {indicadores.map(({ g, texto }) => (
                <button
                  key={g}
                  type="button"
                  className={ver === g ? 'principal' : 'secundario'}
                  aria-pressed={ver === g}
                  style={{ minHeight: 44, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}
                  onClick={() => lista.fijarVarios({ ver: g === 'todas' ? '' : g })}
                >
                  <strong style={{ fontSize: 18 }}>{cuenta(g)}</strong>
                  <span className="pequena">{texto}</span>
                </button>
              ))}
            </div>

            {todasDelDia.length === 0 ? (
              <Vacio titulo={fecha === hoy ? 'No hay órdenes para hoy en este centro.' : 'No hay órdenes ese día en este centro.'} />
            ) : ots.length === 0 ? (
              <div>
                <Vacio titulo={`Ninguna OT coincide con «${busqueda.trim()}».`} />
                <button type="button" style={{ minHeight: 44 }} onClick={() => lista.fijarVarios({ q: '' })}>Borrar búsqueda</button>
              </div>
            ) : visibles.length === 0 ? (
              <Vacio titulo="Ninguna orden en este grupo." />
            ) : (
              <ul aria-label="Órdenes del día" style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {visibles.map((ev) => (
                  <TarjetaHoy key={ev.idOt} ev={ev} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}

type GrupoMnt = 'hoy' | 'atrasados' | 'pendientes' | 'sinTecnico' | 'conflictos' | 'incidencias';
const GRUPOS_MNT: readonly [GrupoMnt, string][] = [
  ['hoy', 'Partes del día'], ['atrasados', 'Atrasados'], ['pendientes', 'Para retomar'], ['sinTecnico', 'Sin técnico'],
  //  F12.3: lo que choca hoy (otro parte, limpieza, disponibilidad), explicado en la tarjeta.
  ['conflictos', 'Con conflicto'], ['incidencias', 'Incidencias por atender'],
];

/**
 * F12.2 — Mantenimiento en «Hoy»: lo que pide atención en el centro, sin ser un segundo Planner. Cada tarjeta lleva
 * al parte o a la incidencia (con «Volver a Hoy»), donde están las acciones (asignar, planificar, resolver).
 */
function BloqueMantenimiento({ idCentro, fecha, grupo, fijarGrupo }: {
  idCentro: string; fecha: string; grupo: string; fijarGrupo: (g: string) => void;
}): ReactNode {
  const datos = useDatos(
    () => api.get<HoyMantenimiento>(`/api/mantenimiento/hoy?idCentro=${idCentro}${fecha === '' ? '' : `&fecha=${fecha}`}`),
    [idCentro, fecha],
  );
  const volver = useVolverAqui('Hoy');
  const g = (GRUPOS_MNT.some(([k]) => k === grupo) ? grupo : 'hoy') as GrupoMnt;
  if (datos.cargando && datos.datos === null) return <Cargando texto="Cargando mantenimiento…" />;
  if (datos.error !== null) return <ErrorCaja error={datos.error} reintentar={datos.recargar} />;
  const d = datos.datos;
  if (d === null) return null;
  const conflictos = d.conflictos ?? [];
  const cuenta = (k: GrupoMnt): number => (k === 'incidencias' ? d.incidencias.length : k === 'conflictos' ? conflictos.length : d[k].length);
  const partes: ParteResumen[] = g === 'incidencias' || g === 'conflictos' ? [] : d[g];
  return (
    <div className="bloque-mantenimiento">
      <div className="fila" role="group" aria-label="Resumen de mantenimiento" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {GRUPOS_MNT.map(([k, t]) => (
          <button key={k} type="button" className={g === k ? 'principal' : 'secundario'} aria-pressed={g === k}
            style={{ minHeight: 44, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}
            onClick={() => fijarGrupo(k === 'hoy' ? '' : k)}>
            <strong style={{ fontSize: 18 }}>{cuenta(k)}</strong>
            <span className="pequena">{t}</span>
          </button>
        ))}
      </div>
      {g === 'conflictos' ? (
        conflictos.length === 0 ? <Vacio titulo="Hoy no choca nada." /> : (
          <ul className="lista-mis-partes" aria-label="Con conflicto" style={{ marginTop: 12 }}>
            {conflictos.map((x) => (
              <li key={x.parte.id}>
                <TarjetaParte parte={x.parte} volver={volver} />
                <div className="planner-mnt-conflicto" role="note"><Alertas alertas={x.alertas} /></div>
              </li>
            ))}
          </ul>
        )
      ) : g === 'incidencias' ? (
        d.incidencias.length === 0 ? <Vacio titulo="Ninguna incidencia espera nada." /> : (
          <ul className="lista-mis-partes" aria-label="Incidencias por atender" style={{ marginTop: 12 }}>
            {d.incidencias.map((i) => (
              <li key={i.id}>
                <Link className="tarjeta-ot tarjeta-parte" state={volver}
                  to={`/mantenimiento/incidencias/${i.id}${i.situacion === 'POR_RESOLVER' ? '?resolver=1' : ''}`}>
                  <div className="tarjeta-ot-cabecera">
                    <span className="tarjeta-ot-fecha">{i.titulo}</span>
                    <PrioridadEtiqueta prioridad={i.prioridad} />
                  </div>
                  <span className="tarjeta-ot-secundario">
                    {i.situacion === 'SIN_PARTE' ? 'Sin parte de trabajo: decide si hace falta uno.' : 'Todo su trabajo está terminado: resuélvela.'}
                  </span>
                  <span className="pequena tenue">{i.codigo}</span>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : partes.length === 0 ? <Vacio titulo="Nada en este grupo." /> : (
        <ul className="lista-mis-partes" aria-label={GRUPOS_MNT.find(([k]) => k === g)?.[1]} style={{ marginTop: 12 }}>
          {partes.map((p) => <li key={p.id}><TarjetaParte parte={p} volver={volver} /></li>)}
        </ul>
      )}
    </div>
  );
}

function TarjetaHoy({ ev }: { ev: PlannerEventoMaterializado }): ReactNode {
  const ubicacion = useLocation();
  const [tareas, setTareas] = useState(false);
  const atencion = atencionDeOt(ev);
  //  Sin asignados no se repite aquí: ya lo dice la etiqueta de atención «Sin asignar».
  const asignados = ev.operarios.length === 0 ? null : nombresOperarios(ev.operarios, 2);
  const trabajo = trabajoDeOt(ev);
  const sala = ev.descripcionSala !== undefined && ev.descripcionSala !== '' && ev.descripcionSala !== ev.codigoSala ? ev.descripcionSala : null;
  return (
    <li className="tarjeta-ot">
      <div className="tarjeta-ot-cabecera">
        <Link
          to={`/ots/${ev.idOt}`}
          state={{ volver: { a: `${ubicacion.pathname}${ubicacion.search}`, texto: 'Hoy' } }}
          className="tarjeta-ot-codigo"
          aria-label={`Abrir OT ${ev.codigo}`}
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }}
        >
          {ev.codigo}
        </Link>
        <EstadoOt estado={ev.estado} />
      </div>
      {trabajo !== null && (
        <div className="tarjeta-ot-trabajo">
          <strong>{trabajo.titulo}</strong>
          {trabajo.mas > 0 && <span className="suave pequena"> · y {trabajo.mas === 1 ? '1 tarea más' : `${String(trabajo.mas)} tareas más`}</span>}
        </div>
      )}
      <div className="tarjeta-ot-sala">
        {sala !== null && <>{sala} · </>}<span className="cod">{ev.codigoSala}</span>
      </div>
      <div className="tarjeta-ot-datos">
        <div className="tarjeta-ot-fecha">{horarioDeOt(ev)}</div>
        <div>{textoAvance(ev)}</div>
        {asignados !== null && <div className="suave pequena">{asignados}</div>}
      </div>
      {atencion.length > 0 && (
        <div className="tarjeta-ot-secundario" aria-label="Requiere atención">
          {atencion.map((a) => (
            <span key={a.clave} className={`etiqueta ${a.tono}`}>{a.texto}</span>
          ))}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <button type="button" style={{ minHeight: 44 }} aria-expanded={tareas} onClick={() => setTareas((v) => !v)}>
          {tareas ? 'Ocultar tareas' : 'Ver tareas'}
        </button>
      </div>
      {tareas && <TareasOt idOt={ev.idOt} />}
    </li>
  );
}

/**
 * Vista rápida de las tareas de UNA OT (ficha pedida a demanda): ubicación, tarea, estado de la
 * línea y asignados por nombre. No duplica la ficha: sin acciones, sin materiales, sin evidencias.
 */
export function TareasOt({ idOt }: { idOt: string }): ReactNode {
  const ot = useDatos(() => api.get<OtDetalle>(`/api/ots/${idOt}`), [idOt]);
  if (ot.cargando && ot.datos === null) return <Cargando texto="Cargando tareas…" />;
  if (ot.error !== null || ot.datos === null) return <ErrorCaja error={ot.error} reintentar={ot.recargar} />;
  if (ot.datos.lineas.length === 0) return <p className="suave pequena">Esta orden no tiene tareas.</p>;
  return (
    <ul aria-label="Tareas" style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {ot.datos.lineas.map((l) => (
        <li key={l.id} className="fila" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <EstadoLinea estado={l.estado} />
          <span>{l.descripcionTarea ?? l.tipoDescripcion}</span>
          <span className="cod pequena">{l.ubicacionCodigo}</span>
          {l.asignados.length > 0 && <span className="suave pequena">{nombresOperarios(l.asignados, 2)}</span>}
        </li>
      ))}
    </ul>
  );
}
